// contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api, { renewToken } from '../utils/api';
import { hasNativeUploader, nativeConfigure } from '../utils/nativeBridge';
import { isTabletMode } from '../utils/tabletMode';
import { clearUserApiCache } from '../utils/apiCache';
import {
  rememberOfflineLogin, offlineLogin, sealOfflineSession, forgetOfflineLogin, updateOfflineProfile,
  matchesOfflinePassword,
} from '../utils/offlineAuth';

const AuthContext = createContext(null);

const SESSION_TIMEOUT_ROLES = ['Reviewer', 'Supervisor', 'Admin', 'R&D', 'R&D Reviewer'];

// Desk roles (review, admin) sign out after 15 minutes without input. Photographers
// never do: a factory tablet lies idle between orders and has to stay signed in.
export function idleTimeoutApplies(user) {
  return !!user && SESSION_TIMEOUT_ROLES.includes(user.role);
}

// Offline sign-in is for the factory floor: every account on an installed tablet,
// and photographers anywhere. A desk account in a normal browser leaves nothing
// behind on the computer.
function offlineLoginAllowed(user) {
  return isTabletMode() || !idleTimeoutApplies(user);
}

// The server did not answer as our API: no connection, a timeout, a gateway error
// or a wifi portal page. Anything else (wrong password, rate limit) is an answer.
function serverUnreachable(err) {
  if (!err.response) return true;
  return err.response.status >= 500 || typeof err.response.data?.error !== 'string';
}

// The server itself said these credentials are wrong (not a rate limit, not a
// gateway): whatever this tablet remembers for that username is out of date.
function credentialsRejected(err) {
  return err.response?.status === 401 && !serverUnreachable(err);
}

// Who signed in last on this device. Survives sign-out, so the next sign-in can
// tell whether the cached API answers belong to someone else.
const LAST_USERNAME_KEY = 'last_session_username';
const sameUsername = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

function readStoredUser() {
  try { return JSON.parse(localStorage.getItem('auth_user')); } catch { return null; }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(readStoredUser);
  const [loading, setLoading] = useState(false);
  // Why the last session ended ('rejected' = the server turned it away); the
  // login screen explains it instead of showing a bare form.
  const [sessionNotice, setSessionNotice] = useState(null);

  // A session that was already open before this key existed: record its owner,
  // so the same user signing in again later keeps the cached orders.
  useEffect(() => {
    const current = readStoredUser();
    try {
      if (current?.username && !localStorage.getItem(LAST_USERNAME_KEY)) {
        localStorage.setItem(LAST_USERNAME_KEY, current.username);
      }
    } catch {}
  }, []);

  // Keep the native upload service's token current so WorkManager can still
  // authenticate uploads after the WebView is gone. Deliberately NOT cleared on
  // logout: photos queued during the shift must keep uploading overnight after
  // the photographer signs out (the token itself expires within 24h anyway).
  useEffect(() => {
    if (!hasNativeUploader()) return;
    const token = localStorage.getItem('auth_token');
    if (token) nativeConfigure({ token });
  }, [user]);

  // On app start, every time the tablet comes back to the foreground and every
  // time the connection returns, renew the token first (the server accepts a
  // recently expired one here). The renewed token reaches localStorage and the
  // native uploader via the api interceptor, so queued orders and photos never
  // sit on 401 after a day without a login. This is also what turns an offline
  // sign-in back into a normal session; if the server refuses, the api
  // interceptor ends the session and the login screen asks for an online sign-in.
  useEffect(() => {
    if (!user) return;
    const renew = () => {
      if (document.visibilityState !== 'visible') return;
      renewToken({ force: true });
    };
    renew();
    document.addEventListener('visibilitychange', renew);
    window.addEventListener('online', renew);
    return () => {
      document.removeEventListener('visibilitychange', renew);
      window.removeEventListener('online', renew);
    };
  }, [user]);

  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);

  // Signing out only ends the session. The upload queue (IndexedDB and native)
  // belongs to the device, not to the user, and is never touched here: whoever
  // signs in next uploads what is still waiting.
  const logout = useCallback((silent = false, reason = null) => {
    const current = readStoredUser();
    let sealed = Promise.resolve();
    if (reason === 'rejected') {
      forgetOfflineLogin(current?.username);
    } else {
      sealed = sealOfflineSession(current?.username, localStorage.getItem('auth_token'));
    }
    setSessionNotice(reason);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    setUser(null);
    clearTimeout(timeoutRef.current);
    clearTimeout(warningRef.current);
    setShowTimeoutWarning(false);
    // The reload waits for the token to be sealed, or it would be cut off halfway.
    if (!silent) sealed.finally(() => { window.location.href = '/'; });
  }, []);

  const resetInactivityTimer = useCallback(() => {
    if (!idleTimeoutApplies(user)) return;

    clearTimeout(timeoutRef.current);
    clearTimeout(warningRef.current);
    setShowTimeoutWarning(false);

    const timeout = 15 * 60 * 1000; // 15 min
    const warning = (15 - 2) * 60 * 1000; // 13 min

    warningRef.current = setTimeout(() => setShowTimeoutWarning(true), warning);
    timeoutRef.current = setTimeout(() => logout(false), timeout);
  }, [user, logout]);

  useEffect(() => {
    if (user) resetInactivityTimer();
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => document.addEventListener(e, resetInactivityTimer, { passive: true }));
    return () => {
      events.forEach(e => document.removeEventListener(e, resetInactivityTimer));
      clearTimeout(timeoutRef.current);
      clearTimeout(warningRef.current);
    };
  }, [user, resetInactivityTimer]);

  useEffect(() => {
    const onForcedLogout = (event) => logout(true, event.detail?.reason || null);
    window.addEventListener('auth:logout', onForcedLogout);
    return () => window.removeEventListener('auth:logout', onForcedLogout);
  }, [logout]);

  // The service worker keeps API answers per URL, not per user. When another user
  // signs in, the previous user's orders and profile are removed before the app
  // can read them offline. Reference data and the upload queue stay.
  const startSession = async (sessionUser, token) => {
    let previous = null;
    try { previous = localStorage.getItem(LAST_USERNAME_KEY); } catch {}
    if (!sameUsername(previous, sessionUser.username)) {
      try { await clearUserApiCache(); } catch {}
      try { localStorage.setItem(LAST_USERNAME_KEY, sessionUser.username); } catch {}
    }
    if (token) localStorage.setItem('auth_token', token);
    else localStorage.removeItem('auth_token');
    localStorage.setItem('auth_user', JSON.stringify(sessionUser));
    setSessionNotice(null);
    setUser(sessionUser);
    return sessionUser;
  };

  const loginOnline = async (username, password) => {
    const { data } = await api.post('/api/auth/login', { username, password });
    if (!data?.token || !data?.user) throw new Error('unexpected login response');
    // A temporary password is not kept: the forced change needs the server anyway.
    // It also means the password was reset, so the one this tablet remembers for
    // offline sign-in must stop working here.
    if (data.user.must_change_password) {
      forgetOfflineLogin(data.user.username);
    } else if (offlineLoginAllowed(data.user)) {
      await rememberOfflineLogin({ username: data.user.username, password, user: data.user, token: data.token }).catch(() => {});
    }
    return startSession(data.user, data.token);
  };

  // When the server cannot be reached the password is checked against what the last online
  // login left on this device. The session resumes with that login's token; the
  // renew effect above restores it with the server once the connection is back.
  const loginOffline = async (username, password) => {
    const { user: cachedUser, token } = await offlineLogin(username, password);
    return startSession(cachedUser, token);
  };

  const login = async (username, password) => {
    setLoading(true);
    try {
      // Always ask the server first. navigator.onLine is not consulted: the tablet
      // WebView reports offline while the server is reachable, and a tablet without
      // an offline record yet could then not sign in at all. Only when the server
      // does not answer does the device decide; an answer from the server (wrong
      // password, rate limit) is final and never falls back to the offline record.
      try {
        return await loginOnline(username, password);
      } catch (err) {
        // The server refused the password this tablet remembers: it was changed, so it
        // must stop working offline here. A mistyped password leaves the record alone.
        if (credentialsRejected(err) && await matchesOfflinePassword(username, password)) forgetOfflineLogin(username);
        if (!serverUnreachable(err)) throw err;
        return await loginOffline(username, password);
      }
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    await api.post('/api/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
    const updated = { ...user, must_change_password: false };
    localStorage.setItem('auth_user', JSON.stringify(updated));
    setUser(updated);
    if (offlineLoginAllowed(updated)) {
      await rememberOfflineLogin({
        username: updated.username, password: newPassword, user: updated, token: localStorage.getItem('auth_token'),
      }).catch(() => {});
    }
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get('/api/auth/me');
      localStorage.setItem('auth_user', JSON.stringify(data));
      updateOfflineProfile(data);
      setUser(data);
    } catch {}
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, sessionNotice, changePassword, refreshUser, showTimeoutWarning, setShowTimeoutWarning, resetInactivityTimer }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
